import { DropdownIcon } from '@components/icons/DropdownIcon';
import { createTheme } from '@mui/material/styles';
import { PaletteOptions as MuiPaletteOptions } from '@mui/material/styles';
import React from 'react';

import { ReactComponent as CheckboxChecked } from './images/Checkbox.svg';
import { ReactComponent as CheckboxUnchecked } from './images/Unchecked.svg';
import brandPalette, { WithColorPalette } from './palette';
import { generateShadows } from './shadows';
const SEMIBOLD_WEIGHT = 600;

function webfontCollection(weight: string) {
  return ['ttf', 'woff2']
    .map((format) => `url("/fonts/manrope-${weight}.${format}") format("${format}")`)
    .join(',\n');
}

const uiFonts = [
  {
    fontFamily: 'Manrope',
    fontWeight: 'normal',
    src: webfontCollection('regular'),
  },
  {
    fontFamily: 'Manrope',
    fontWeight: SEMIBOLD_WEIGHT,
    src: webfontCollection('semibold'),
  },
  {
    fontFamily: 'Manrope',
    fontWeight: 'bold',
    src: webfontCollection('bold'),
  },
  {
    fontFamily: 'Manrope',
    fontWeight: 900,
    src: webfontCollection('extrabold'),
  },
];

declare module '@mui/material/styles' {
  interface Theme {
    mainShadows: {
      none: string;
      surface: string;
      modal: string;
    };
    /** Box Shadows which are used to indicate visual focus */
    focusRings: {
      primary: string;
      idle: string;
      create: (color: string, outside?: boolean) => string;
    };
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    mainShadows?: {
      none: string;
      surface: string;
      modal: string;
    };
    focusRings?: {
      primary: string;
      /** use this focus ring when not focused */
      idle: string;
      create: (color: string, outside?: boolean) => string;
    };
  }
}

declare module '@mui/material/styles' {
  interface Shape {
    /**
     * The border radius of content within a surface, usually UI controls,
     * videos, etc
     */
    contentBorderRadius: number;
    /**
     * The border radius of items nested within content, within a surface
     */
    innerBorderRadius: number;
    borderWidth: number;
    surfaceBorderRadius: number;
  }
}

declare module '@mui/material/styles' {
  interface PaletteOptions {
    brandColors?: typeof brandPalette;
  }

  interface Palette {
    brandColors: typeof brandPalette;
  }

  interface BreakpointOverrides {
    xs: true; // removes the `xs` breakpoint
    sm: true;
    md: true;
    lg: true;
    xl: false; // removes the `xl` breakpoint
  }
}

function toMuiColorPalette(pal: WithColorPalette) {
  return {
    light: pal.light,
    main: pal.regular,
    dark: pal.bold,
    contrastText: pal.ink,
  };
}

function createFocusRing(color: string, outside: boolean = false) {
  return `${!outside ? 'inset ' : ''}0 0 0 2px ${color}`;
}

/**
 *
 * @param colors Color palette configuration
 */
const createPaletteTheme = (colors: { primary: WithColorPalette; secondary?: WithColorPalette }) => {
  const finalPalette: MuiPaletteOptions = {
    // all themes use this grey, for now.
    grey: {
      50: brandPalette.slate.light,
      100: brandPalette.slate.light,
      200: brandPalette.slate.light,
      300: brandPalette.slate.regular,
      400: brandPalette.slate.regular,
      500: brandPalette.slate.regular,
      600: brandPalette.slate.bold,
      700: brandPalette.slate.bold,
      800: brandPalette.slate.ink,
      900: brandPalette.slate.ink,
      A100: brandPalette.slate.light,
      A200: brandPalette.slate.regular,
      A400: brandPalette.slate.bold,
      A700: brandPalette.slate.ink,
    },
    background: {
      default: brandPalette.snow.regular,
      paper: brandPalette.snow.regular,
    },
    text: {
      primary: brandPalette.ink.regular,
      secondary: brandPalette.slate.ink,
    },
    success: toMuiColorPalette(brandPalette.oregano),
    error: toMuiColorPalette(brandPalette.cherry),
    common: {
      white: brandPalette.snow.regular,
      black: brandPalette.ink.regular,
    },
    primary: toMuiColorPalette(colors.primary),
    secondary: toMuiColorPalette(colors.secondary || brandPalette.oregano),
    brandColors: brandPalette,
  };

  // only used to extract some MUI theme tools that can be
  // used in the overrides below, such as computed color palettes and
  // font sizing utils
  const { typography, palette, spacing, transitions, shape, mainShadows, focusRings, zIndex, breakpoints } =
    createTheme({
      palette: finalPalette,
      typography: {
        fontWeightMedium: SEMIBOLD_WEIGHT,
      },
      shape: {
        borderRadius: 6,
        contentBorderRadius: 4,
        innerBorderRadius: 2,
        borderWidth: 2,
        surfaceBorderRadius: 14,
      } as any,
      mainShadows: {
        // an invisible / nonexistent shadow of the same arity as the others, for easing animations
        none: '0 0 0 transparent, 0 0 0 transparent, 0 0 0 transparent, 0 0 0 transparent',
        surface: `0px 45px 89px rgba(0, 0, 0, 0.0185187), 0px 14.27px 26.8309px rgba(0, 0, 0, 0.0225848), 0px 6.11599px 11.1442px rgba(0, 0, 0, 0.0264039), 0px 2.11191px 4.03063px rgba(0, 0, 0, 0.04)`,
        modal: `0px 15px 80px rgba(0, 0, 0, 0.08), 0px 4.52206px 24.1177px rgba(0, 0, 0, 0.0521271), 0px 1.87823px 10.0172px rgba(0, 0, 0, 0.04), 0px 0.67932px 3.62304px rgba(0, 0, 0, 0.0278729)`,
      },
      focusRings: {
        primary: createFocusRing(brandPalette.oregano.bold),
        create: createFocusRing,
        idle: `inset 0 0 0 0 transparent`,
      },
    });

  const finalShadows = generateShadows();
  finalShadows[1] = mainShadows.surface;
  finalShadows[2] = mainShadows.modal;

  const theme = createTheme({
    spacing,
    breakpoints: {
      values: {
        xs: 300,
        sm: 440,
        md: 850,
        // accommodates the entire room controls with all available features and labels in English
        lg: 1080,
      },
    },

    typography: {
      fontFamily: 'Manrope, system-ui, Helvetica, sans-serif',
      fontWeightMedium: SEMIBOLD_WEIGHT,
      body1: {
        lineHeight: 22 / 16,
        fontSize: typography.pxToRem(16),
      },
      body2: {
        fontSize: typography.pxToRem(13),
        lineHeight: 16 / 13,
      },
      caption: {
        fontSize: typography.pxToRem(13),
        lineHeight: 16 / 13,
        color: palette.grey[900],
      },
      h1: {
        fontSize: typography.pxToRem(42),
        lineHeight: 48 / 42,
        fontWeight: typography.fontWeightBold as any,
        color: brandPalette.vintageInk.regular,
      },
      h2: {
        fontSize: typography.pxToRem(24),
        lineHeight: 30 / 24,
        fontWeight: typography.fontWeightMedium as any,
      },
      h3: {
        fontSize: typography.pxToRem(16),
        lineHeight: 22 / 16,
        fontWeight: typography.fontWeightMedium as any,
      },
      h4: {
        fontSize: typography.pxToRem(13),
        lineHeight: 16 / 13,
        fontWeight: typography.fontWeightMedium as any,
      },
      button: {
        fontSize: typography.pxToRem(16),
        fontWeight: typography.fontWeightMedium as any,
        lineHeight: 22 / 16,
        textTransform: 'none',
      },
    },
    shadows: finalShadows,
    mainShadows,
    shape,
    palette: finalPalette,
    focusRings,
    components: {
      MuiButton: {
        defaultProps: {
          // default button is the filled version
          variant: 'contained',
          // default to primary color
          color: 'primary',
          // remove some MUI effects
          disableRipple: true,
          disableTouchRipple: true,
          // default button to take full container width
          fullWidth: true,
        },
      },
      MuiFab: {
        defaultProps: {
          disableFocusRipple: true,
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiIconButton: {
        defaultProps: {
          color: 'inherit',
          disableFocusRipple: true,
          disableTouchRipple: true,
          disableRipple: true,
        },
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: 'inherit',
          color: 'inherit',
        },
      },
      MuiInputLabel: {
        defaultProps: {
          disableAnimation: true,
          shrink: true,
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          notched: false,
        },
      },
      MuiFilledInput: {
        defaultProps: {
          disableUnderline: true,
        },
        styleOverrides: {
          root: {
            borderRadius: (shape as any).contentBorderRadius,
            borderTopLeftRadius: (shape as any).contentBorderRadius,
            borderTopRightRadius: (shape as any).contentBorderRadius,

            backgroundColor: palette.grey[50],

            boxShadow: focusRings.idle,
            transition: transitions.create(['box-shadow', 'background-color', 'color', 'caret-color']),

            '&:hover': {
              backgroundColor: palette.grey[100],
            },

            '&.Mui-error': {
              backgroundColor: palette.error.light,
            },

            '&:focus, &.Mui-focused': {
              boxShadow: focusRings.primary,
              backgroundColor: palette.background.paper,
              caretColor: palette.secondary.contrastText,
              '&.Mui-error': {
                boxShadow: focusRings.primary,
                caretColor: palette.error.contrastText,
              },
            },

            '&.Mui-disabled': {
              backgroundColor: palette.background.paper,
              boxShadow: focusRings.create(palette.grey[50]),
              color: palette.grey[700],
              opacity: 1,
            },
          },
          input: {
            padding: '14.5px 14px',
            '&:-webkit-autofill': {
              borderRadius: 'inherit',
            },
            '&::placeholder': {
              color: palette.grey[900],
              opacity: 1,
            },
          },
          inputSizeSmall: {
            paddingTop: 6,
          },
          inputHiddenLabel: {
            paddingTop: '14.5px',
            paddingBottom: '14.5px',
          },
          inputMultiline: {
            padding: '10px 12px',
          },
        },
      },
    },
  });

  // Explicitly ensure spacing is a function
  if (typeof theme.spacing !== 'function') {
    theme.spacing = spacing || ((factor: number) => `${8 * factor}px`);
  }

  return theme;
};

// eslint-disable-next-line no-shadow
export enum ThemeName {
  Mandarin = 'mandarin',
  Cherry = 'cherry',
  Oregano = 'oregano',
  Lavender = 'lavender',
  Snow = 'snow',
  Blueberry = 'blueberry',
  Slate = 'slate',
  VintageInk = 'vintageInk',
}

// kind of arbitrary color combos below...
export const mandarin = createPaletteTheme({
  primary: brandPalette.mandarin,
});

export const cherry = createPaletteTheme({
  primary: brandPalette.cherry,
});

export const lavender = createPaletteTheme({
  primary: brandPalette.lavender,
});

export const oregano = createPaletteTheme({
  primary: brandPalette.oregano,
});

export const snow = createPaletteTheme({
  primary: brandPalette.snow,
});

export const slate = createPaletteTheme({
  primary: brandPalette.slate,
});

export const blueberry = createPaletteTheme({
  primary: brandPalette.blueberry,
});

export const vintageInk = createPaletteTheme({
  primary: brandPalette.vintageInk,
});

export const getThemeFromName = (themeName: ThemeName | undefined) => {
  switch (themeName) {
    case ThemeName.Mandarin:
      return mandarin;
    case ThemeName.Cherry:
      return cherry;
    case ThemeName.Oregano:
      return oregano;
    case ThemeName.Lavender:
      return lavender;
    case ThemeName.Snow:
      return snow;
    case ThemeName.Blueberry:
      return blueberry;
    case ThemeName.VintageInk:
      return vintageInk;
    default:
      return mandarin;
  }
};
